import { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';
import queryString from 'query-string';
import { faCheckCircle } from '@fortawesome/pro-solid-svg-icons';
import { InPageNotification } from '@ebsco-ui/ebsco-ui';

import { NotificationProps } from '@app/components';
import { useFeatureFlag, useLogin, useSSOLogin } from '@app/hooks';
import { APIError } from '@app/utils';
import { sharedMessages } from '@app/translations';
import { accountActivationStatus, Feature, LoginMethod } from '@app/constants';

interface UseAuthenticationProps {
  method?: LoginMethod;
  passwordFieldName?: string;
  shouldCheckSSO?: boolean;
}

interface AuthenticationUtils {
  notification: NotificationProps | undefined;
  isLoading: boolean;
  isSuccess: boolean;
  isSSOEnabled: boolean;
  isSSOError: boolean;
  loginUser: (values?, form?) => void;
}

export const useAuthentication = ({
  passwordFieldName,
  method = LoginMethod.native,
  shouldCheckSSO = false,
}: UseAuthenticationProps): AuthenticationUtils => {
  const { isActive: isConsortiaLogin } = useFeatureFlag({
    name: Feature.consortiaLoginFlow,
  });
  const [formError, setFormError] = useState<string | null>(null);
  const { search: urlQuery } = useLocation();
  const { $t } = useIntl();
  const { loginMutation, directLoginMutation } = useLogin({
    isConsortiaLogin,
  });

  const notifications = {
    ACCOUNT_ACTIVATED: {
      variant: InPageNotification.VARIANT.success,
      description: $t({
        id: 'auth.accountActivation.success',
        defaultMessage: 'Thank you for verifying your email.',
      }),
      icon: faCheckCircle,
    },
  };

  const getFormErrorMessage = error =>
    error instanceof APIError
      ? error.message
      : $t(sharedMessages.somethingWentWrongContactAdmin);

  const { ssoMutation, samlCheckQuery } = useSSOLogin({
    shouldCheckSSO,
    setFormError,
    getFormErrorMessage,
  });

  const isSSOLogin = method === LoginMethod.sso;
  const isSSOError =
    Number(queryString.parse(urlQuery)?.errorCode) >= 400 ||
    ssoMutation.isError;

  const methodMap = {
    [LoginMethod.direct]: directLoginMutation,
    [LoginMethod.native]: loginMutation,
    [LoginMethod.sso]: ssoMutation,
  };

  const loginUser = (values, form) => {
    setFormError(null);

    methodMap[method].mutate(values, {
      onError: error => {
        setFormError(getFormErrorMessage(error));

        form.setValue(passwordFieldName, '');
        form.clearErrors(passwordFieldName);
      },
    });
  };

  const notification = useMemo(() => {
    const { status } = queryString.parse(urlQuery);

    if (status === accountActivationStatus.activated) {
      return notifications.ACCOUNT_ACTIVATED;
    }

    if (formError) {
      return {
        variant: InPageNotification.VARIANT.error,
        description: formError,
      };
    }

    return undefined;
  }, [formError, notifications.ACCOUNT_ACTIVATED, urlQuery]);

  useEffect(() => {
    if (!isSSOError) {
      setFormError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlQuery]);

  useEffect(() => {
    if (isSSOError) {
      setFormError($t(sharedMessages.somethingWentWrongContactAdmin));
    }
  }, [$t, isSSOError]);

  return {
    notification,
    isLoading: isSSOLogin ? ssoMutation.isLoading : methodMap[method].isLoading,
    isSuccess: isSSOLogin ? ssoMutation.isSuccess : methodMap[method].isSuccess,
    isSSOEnabled: Boolean(samlCheckQuery.data?.active),
    isSSOError,
    loginUser: isSSOLogin ? ssoMutation.mutate : loginUser,
  };
};
